import {Injectable} from '@angular/core';
import {ProjectViewModel} from '../../features/user/garage/project.view-model';
import {ProjectPerformanceMetrics} from '../../features/ranking/project-performance-metrics.interface';

@Injectable({
  providedIn: 'root'
})
export class ProjectPerformanceMetricsService {
  public getStrongestProject(projects: ProjectViewModel[]): ProjectViewModel | null {
    const ranges = this.getPerformanceRanges(projects);
    const scoredProjects = projects
      .map((project) => ({
        project,
        score: this.getPerformanceScore(project, ranges),
      }))
      .filter(({ score }) => score !== null);

    if (!scoredProjects.length) {
      return null;
    }

    scoredProjects.sort((left, right) => {
      const scoreCompare = (right.score ?? 0) - (left.score ?? 0);
      if (scoreCompare !== 0) {
        return scoreCompare;
      }

      const rightMetrics = this.getEffectiveMetrics(right.project);
      const leftMetrics = this.getEffectiveMetrics(left.project);
      const powerCompare = (rightMetrics.power ?? 0) - (leftMetrics.power ?? 0);
      if (powerCompare !== 0) {
        return powerCompare;
      }

      const speedCompare = (rightMetrics.maxSpeed ?? 0) - (leftMetrics.maxSpeed ?? 0);
      if (speedCompare !== 0) {
        return speedCompare;
      }

      return (leftMetrics.acceleration ?? Number.MAX_SAFE_INTEGER) - (rightMetrics.acceleration ?? Number.MAX_SAFE_INTEGER);
    });

    return scoredProjects[0].project;
  }

  public getStrongestStats(project: ProjectViewModel | null): ProjectPerformanceMetrics | null {
    if (!project) {
      return null;
    }

    const metrics = this.getEffectiveMetrics(project);
    if (metrics.power === undefined && metrics.maxSpeed === undefined && metrics.acceleration === undefined) {
      return null;
    }

    return metrics;
  }

  public getPerformanceScore(
    project: ProjectViewModel,
    ranges: { maxPower: number | null; maxSpeed: number | null; minAccel: number | null; maxAccel: number | null }
  ): number | null {
    const metrics = this.getEffectiveMetrics(project);
    const normalized: number[] = [];

    if (metrics.power !== undefined && ranges.maxPower) {
      normalized.push(metrics.power / ranges.maxPower);
    }
    if (metrics.maxSpeed !== undefined && ranges.maxSpeed) {
      normalized.push(metrics.maxSpeed / ranges.maxSpeed);
    }
    if (
      metrics.acceleration !== undefined
      && ranges.minAccel !== null
      && ranges.maxAccel !== null
      && ranges.maxAccel !== ranges.minAccel
    ) {
      normalized.push((ranges.maxAccel - metrics.acceleration) / (ranges.maxAccel - ranges.minAccel));
    }

    if (!normalized.length) {
      return null;
    }

    return normalized.reduce((sum, value) => sum + value, 0) / normalized.length;
  }

  public getEffectiveMetrics(project: ProjectViewModel | null): ProjectPerformanceMetrics {
    if (!project) {
      return {};
    }

    const override = project.vehicleParametersOverride;
    const power = this.toNumber(override?.powerHp ?? project?.vehicle?.engine?.enginePowerBHP);
    const maxSpeed = this.toNumber(override?.topSpeed ?? project?.vehicle?.parameter?.maxSpeed);
    const acceleration = this.toNumber(override?.accelerationZeroToHundred ?? project?.vehicle?.parameter?.acceleration);

    return {
      power: power ?? undefined,
      maxSpeed: maxSpeed ?? undefined,
      acceleration: acceleration ?? undefined,
    };
  }

  public getPerformanceRanges(projects: ProjectViewModel[]): { maxPower: number | null; maxSpeed: number | null; minAccel: number | null; maxAccel: number | null } {
    const powerValues = projects
      .map((project) => this.getEffectiveMetrics(project).power)
      .filter((value): value is number => value !== undefined);
    const speedValues = projects
      .map((project) => this.getEffectiveMetrics(project).maxSpeed)
      .filter((value): value is number => value !== undefined);
    const accelValues = projects
      .map((project) => this.getEffectiveMetrics(project).acceleration)
      .filter((value): value is number => value !== undefined);

    return {
      maxPower: powerValues.length ? Math.max(...powerValues) : null,
      maxSpeed: speedValues.length ? Math.max(...speedValues) : null,
      minAccel: accelValues.length ? Math.min(...accelValues) : null,
      maxAccel: accelValues.length ? Math.max(...accelValues) : null,
    };
  }

  private toNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string') {
      const normalized = value.replace(',', '.');
      const parsed = Number(normalized.match(/-?\d+(?:\.\d+)?/)?.[0] ?? Number.NaN);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }
}
